import { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { useGetPrintJobExecutorsAllPagesQuery } from '../../../store/api/print-job-executors-api'
import { useGetUsersQuery } from '../../../store/api/users-api'
import { Reasons } from '../../../types/api/print-job-executor'
import { EndpointQueryParameters } from '../../../types/strapi/strapi'
import { buildVolumeSummary } from '../helpers/build-volume-summary'

function buildExecutorsQuery(month: Dayjs): EndpointQueryParameters {
    const monthStart = month.startOf('month').toISOString()
    const monthEnd = month.endOf('month').toISOString()

    return {
        filters: {
            start: { $gte: monthStart, $lte: monthEnd },
            reason: { $eq: Reasons.Done },
        },
        fields: ['documentId', 'start', 'end', 'reason'],
        populate: {
            user: {
                fields: ['documentId'],
            },
            machine: {
                fields: ['name'],
            },
            print_job: {
                fields: ['documentId'],
                populate: {
                    material_consumptions: {
                        fields: ['amount', 'layerNumber'],
                        populate: {
                            material: {
                                fields: ['width'],
                            },
                        },
                    },
                    shipment: {
                        populate: {
                            calculated: true,
                            calculatedOnActual: true,
                        },
                    },
                },
            },
        },
    }
}

export default function useVolumeSummary(month: Dayjs) {
    const executorsQuery = useMemo(() => buildExecutorsQuery(month), [month])

    const {
        data: users,
        isLoading: isUsersLoading,
        isError: isUsersError,
        error: usersError,
    } = useGetUsersQuery({
        fields: ['documentId', 'username', 'shiftProfile'],
    })

    const {
        data: executors,
        isLoading: isExecutorsLoading,
        isError: isExecutorsError,
        error: executorsError,
    } = useGetPrintJobExecutorsAllPagesQuery(executorsQuery)

    const summary = useMemo(
        () => buildVolumeSummary(users ?? [], executors ?? []),
        [users, executors]
    )

    return {
        summary,
        isLoading: isUsersLoading || isExecutorsLoading,
        isError: isUsersError || isExecutorsError,
        error: usersError ?? executorsError,
        hasData: users != null && executors != null,
    }
}
